import { useEffect, useState } from "react";
import type { Deal } from "../types";
import { buildNegotiation, DEFAULT_TARGET_CAP } from "../lib/negotiation";
import { generateOfferLetter, RECIPIENTS, type Recipient } from "../lib/offerLetter";
import { sendOfferLetter } from "../lib/email";
import { listContacts, contactsForDeal, type Contact } from "../lib/contacts";
import { withDiligence, negotiableDefects } from "../lib/diligence";
import { isCloud } from "../lib/supabase";
import { money0, pct, x2 } from "../lib/format";

const SEV_CLS: Record<string, string> = { minor: "t-watch", moderate: "t-watch", major: "t-fail", safety: "t-fail" };

/**
 * The negotiation worksheet. Anchors your revised offer to the LOWER of
 * (asking − documented defects/liens) and (the max price that still hits your
 * target return) — so the number is both justified and profitable. Every line
 * is the backup the AI offer letter will cite.
 */
export function Negotiation({ deal }: { deal: Deal }) {
  const [targetCapPct, setTargetCapPct] = useState(Math.round(DEFAULT_TARGET_CAP * 1000) / 10);
  const n = buildNegotiation(deal, { targetCap: targetCapPct / 100 });

  return (
    <>
      <div className="pe">
        <div className="l">◆ In plain English</div>
        <p>
          Your findings become a number. Every documented defect and lien comes off the asking price — and Beacon
          <b> caps your offer at the highest price that still hits your profit target</b>, so you never overpay for a
          motivated seller. The revised offer is the lower of the two, fully backed by line items. This is what the AI
          letter to the seller/bank will be built from.
        </p>
      </div>

      {/* HEADLINE */}
      <div className="card pad" style={{ marginBottom: 12, borderLeft: "4px solid var(--brass)" }}>
        <div className="grid g3" style={{ alignItems: "end" }}>
          <div>
            <div className="eyebrow">Asking / on the table</div>
            <div className="fr" style={{ fontSize: 22, color: "var(--muted)", fontWeight: 600 }}>{money0(n.asking)}</div>
          </div>
          <div>
            <div className="eyebrow">Your revised offer</div>
            <div className="fr" style={{ fontSize: 30, color: "var(--brass-deep)", fontWeight: 700, lineHeight: 1.1 }}>{money0(n.revisedOffer)}</div>
          </div>
          <div>
            <div className="eyebrow">Reduction</div>
            <div className="fr" style={{ fontSize: 22, color: "var(--forest)", fontWeight: 600 }}>−{money0(n.asking - n.revisedOffer)} <span style={{ fontSize: 13, color: "var(--muted)" }}>({pct(n.discount, 0)})</span></div>
          </div>
        </div>
        <div className="flag ok" style={{ marginTop: 12 }}>
          <span>◆</span>
          <div>
            Your offer is set by <b>{n.binding === "profit" ? "your profit ceiling" : n.binding === "defects" ? "the documented findings" : "the asking price"}</b>.
            {n.binding === "profit" && <> Even before findings, {money0(n.asking)} won't hit a {targetCapPct}% cap — the most you can pay and still make your number is <b>{money0(n.maxProfitPrice)}</b>.</>}
            {n.binding === "defects" && <> The {money0(n.totalReduction)} of documented issues brings a fair price down to <b>{money0(n.reducedPrice)}</b>, and that still pencils.</>}
            {n.binding === "asking" && <> No documented issues and the price already clears your target — this one's fair as-is.</>}
          </div>
        </div>
      </div>

      {/* THE TWO ANCHORS */}
      <div className="grid g2" style={{ marginBottom: 12 }}>
        <div className="card pad">
          <div className="eyebrow">Anchor 1 — documented reductions</div>
          <table className="t" style={{ marginTop: 8 }}>
            <tbody>
              <tr><td>Asking price</td><td className="n">{money0(n.asking)}</td></tr>
              <tr><td>Documented defects &amp; liens</td><td className="n" style={{ color: "var(--fail)" }}>−{money0(n.totalReduction)}</td></tr>
              <tr className="tot"><td>Justified price</td><td className="n">{money0(n.reducedPrice)}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="card pad">
          <div className="eyebrow">Anchor 2 — profit ceiling</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0" }}>
            <span style={{ fontSize: 12.5, color: "#37413c" }}>Target cap rate</span>
            <input type="number" step="0.5" value={targetCapPct} onChange={(e) => setTargetCapPct(Math.max(0, Number(e.target.value) || 0))}
              className="rp-in n" style={{ width: 68, textAlign: "right" }} />
            <span style={{ fontSize: 12.5, color: "var(--muted)" }}>%</span>
          </div>
          {n.maxFeasible ? (
            <table className="t">
              <tbody>
                <tr><td>Max price for {targetCapPct}% cap</td><td className="n">{money0(n.maxProfitPrice)}</td></tr>
                <tr><td>Binding constraint</td><td className="n">{n.binding === "profit" || n.reducedPrice >= n.maxProfitPrice ? "profit ceiling" : "not binding"}</td></tr>
              </tbody>
            </table>
          ) : (
            <div className="flag warn"><span>◐</span><div>At a {targetCapPct}% cap this deal doesn't pencil at <em>any</em> price — your findings are the only lever. Lower the target or walk.</div></div>
          )}
        </div>
      </div>

      {/* BACKUP LINE ITEMS */}
      <div className="card pad" style={{ marginBottom: 12 }}>
        <div className="eyebrow">Your backup — every reduction, documented</div>
        {n.reductions.length === 0 ? (
          <div className="note" style={{ marginTop: 8 }}>No defects or liens recorded yet. Fill the <b>Due Diligence</b> matrix above (or have your inspectors submit) — each defect with a cost lands here as leverage.</div>
        ) : (
          <div className="tablewrap" style={{ marginTop: 8 }}>
            <table className="t">
              <thead><tr><th>Finding</th><th>Severity</th><th>Source</th><th className="n">Reduction</th></tr></thead>
              <tbody>
                {n.reductions.map((r, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500, color: "var(--forest)" }}>{r.label}</td>
                    <td>{r.severity ? <span className={"tag " + (SEV_CLS[r.severity] || "t-watch")}>{r.severity}</span> : <span style={{ color: "var(--muted)" }}>—</span>}</td>
                    <td style={{ fontSize: 12, color: "var(--muted)" }}>{r.source}</td>
                    <td className="n" style={{ color: "var(--fail)" }}>−{money0(r.amount)}</td>
                  </tr>
                ))}
                <tr className="tot"><td colSpan={3}>Total documented leverage</td><td className="n">−{money0(n.totalReduction)}</td></tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* PROOF IT PAYS */}
      <div className="card pad">
        <div className="eyebrow">At your revised offer, this deal still pays</div>
        <div className="grid g3" style={{ marginTop: 8 }}>
          <div><div className="kl">Cap-on-cost</div><div className="kv" style={{ color: n.capAtOffer >= n.targetCap ? "var(--pass)" : "var(--fail)" }}>{pct(n.capAtOffer)}</div></div>
          <div><div className="kl">DSCR</div><div className="kv">{x2(n.dscrAtOffer)}</div></div>
          <div><div className="kl">Discount won</div><div className="kv">{pct(n.discount, 0)}</div></div>
        </div>
      </div>

      {/* AI OFFER LETTER */}
      <OfferLetterCard deal={deal} targetCap={targetCapPct / 100} />
    </>
  );
}

// --- AI offer letter: findings + number -> a firm, data-backed letter --------
function OfferLetterCard({ deal, targetCap }: { deal: Deal; targetCap: number }) {
  const cloud = isCloud();
  const [recipient, setRecipient] = useState<Recipient>("seller");
  const [senderName, setSenderName] = useState("");
  const [senderEntity, setSenderEntity] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [note, setNote] = useState("");
  const [state, setState] = useState<"idle" | "run" | "done" | "err">("idle");
  const [letter, setLetter] = useState("");
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  // send (APAS Build via Resend)
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(`Revised offer — ${deal.name}`);
  const [send, setSend] = useState<{ s: "idle" | "run" | "ok" | "err"; msg?: string }>({ s: "idle" });
  useEffect(() => { listContacts().then((all) => setContacts(contactsForDeal(all, deal.id).filter((c) => c.email))).catch(() => {}); }, [deal.id]);

  const attachments = negotiableDefects(withDiligence(deal));
  const files = attachments.flatMap((d) => (d.attachments ?? []).map((f) => `${d.system}: ${f}`));

  async function sendLetter() {
    setSend({ s: "run" });
    try {
      await sendOfferLetter(deal, to.trim(), letter, subject);
      setSend({ s: "ok", msg: `Sent to ${to.trim()} as APAS Build.` });
    } catch (e) {
      setSend({ s: "err", msg: e instanceof Error ? e.message : String(e) });
    }
  }

  async function gen() {
    setState("run"); setErr("");
    try {
      const r = await generateOfferLetter(deal, { recipient, senderName, senderEntity, recipientName, note }, targetCap);
      setLetter(r.text);
      setState("done");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setState("err");
    }
  }
  function copy() {
    navigator.clipboard?.writeText(letter).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }).catch(() => {});
  }

  return (
    <div className="card pad" style={{ marginTop: 12, borderLeft: "4px solid var(--forest)" }}>
      <div className="eyebrow">✨ AI offer letter — the ask, backed by your findings</div>
      {!cloud && (
        <div className="flag warn" style={{ marginTop: 10 }}>
          <span>◐</span><div>Letter writing uses Claude in the cloud <span className="mono">analyze</span> Edge Function. Sign in / connect cloud mode to generate.</div>
        </div>
      )}
      <div className="bb-grid" style={{ marginTop: 10 }}>
        <label className="bb-f"><span>Recipient</span>
          <select value={recipient} onChange={(e) => setRecipient(e.target.value as Recipient)}>
            {RECIPIENTS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select></label>
        <label className="bb-f"><span>Recipient name (opt.)</span><input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="e.g. Ms. Rivera / REO desk" /></label>
        <label className="bb-f"><span>Your name</span><input value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Hardeep Anand" /></label>
        <label className="bb-f"><span>Your entity (opt.)</span><input value={senderEntity} onChange={(e) => setSenderEntity(e.target.value)} placeholder="APAS Capital LLC" /></label>
      </div>
      <label className="bb-f" style={{ marginTop: 10 }}><span>Extra context (opt.)</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. cash close in 21 days, we've closed 3 like this in the county" /></label>
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 6 }}>{RECIPIENTS.find((r) => r.id === recipient)?.blurb}</div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
        <button className="btn btn-p btn-sm" onClick={gen} disabled={!cloud || state === "run"}>{state === "run" ? "Writing…" : "✨ Generate offer letter"}</button>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>Claude builds the case from your findings — never from your internal return math.</span>
      </div>
      {state === "err" && <div className="flag bad" style={{ marginTop: 10 }}><span>!</span><div>{err}</div></div>}

      {state === "done" && (
        <>
          <textarea value={letter} onChange={(e) => setLetter(e.target.value)} rows={16}
            style={{ width: "100%", marginTop: 12, padding: 14, border: "1px solid var(--line)", borderRadius: 9, fontSize: 13.5, lineHeight: 1.6, fontFamily: "inherit", background: "var(--white)", resize: "vertical" }} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            <button className="btn btn-a btn-sm" onClick={copy}>{copied ? "✓ Copied" : "⧉ Copy letter"}</button>
            <button className="btn btn-g btn-sm" onClick={() => window.print()}>🖨 Print / PDF</button>
            <span style={{ fontSize: 12, color: "var(--muted)", alignSelf: "center" }}>Edit freely before you send.</span>
          </div>

          {/* SEND — as APAS Build */}
          <div style={{ marginTop: 14, borderTop: "1px dashed var(--line)", paddingTop: 12 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Send it — from <b style={{ color: "var(--forest)" }}>APAS Build</b> · offers@apas.dev</div>
            {contacts.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                {contacts.map((c) => (
                  <button key={c.id} className={"chip-toggle" + (to === c.email ? " on" : "")} onClick={() => setTo(c.email!)}>
                    {to === c.email ? "✓ " : ""}{c.name} <span style={{ opacity: 0.7 }}>· {c.type}</span>
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@email.com"
                className="bb-name" style={{ minWidth: 220 }} />
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
                className="bb-name" style={{ minWidth: 220, flex: 1 }} />
              <button className="btn btn-p btn-sm" onClick={sendLetter}
                disabled={!cloud || send.s === "run" || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim())}>
                {send.s === "run" ? "Sending…" : "✉ Send as APAS Build"}
              </button>
            </div>
            {send.s === "ok" && <div className="flag ok" style={{ marginTop: 8 }}><span>✓</span><div>{send.msg}</div></div>}
            {send.s === "err" && <div className="flag warn" style={{ marginTop: 8 }}><span>◐</span><div>{send.msg} <span style={{ color: "var(--muted)" }}>(Needs a Resend key + verified apas.dev domain.)</span></div></div>}
            <div className="note" style={{ marginTop: 8 }}>
              {contacts.length > 0 ? "Pick a linked contact above or type an address. " : "Tip: link this deal's seller/agent in CRM to prefill here. "}
              The letter goes out on your branded <b>apas.dev</b> domain, with your findings as backup.
            </div>
          </div>
        </>
      )}

      {/* attachment manifest */}
      <div style={{ marginTop: 14, borderTop: "1px dashed var(--line)", paddingTop: 12 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Attach with the letter — your documentation</div>
        {attachments.length === 0 ? (
          <div className="note">No documented findings yet. Fill the Due Diligence matrix — each defect (with photos/reports) becomes an attachment that backs your number.</div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#37413c" }}>
            {attachments.map((d) => (
              <li key={d.id} style={{ marginBottom: 3 }}>
                {d.system}: {d.label} — <b>{money0(d.cost ?? 0)}</b>{d.severity ? ` (${d.severity})` : ""}{(d.attachments?.length ?? 0) > 0 ? ` · ${d.attachments!.length} file(s)` : " · no photo yet"}
              </li>
            ))}
          </ul>
        )}
        {files.length > 0 && <div className="note" style={{ marginTop: 6 }}>{files.length} file(s) referenced: {files.join(", ")}</div>}
      </div>
    </div>
  );
}
